import {
  FilterOptionList,
  TextFilterControl,
  VisibleEnumSelect,
  VisibleDateRangeField,
  VisibleTextField,
  formatDateRangeDisplay,
  DateRangeControl,
  isDateRangeActive,
} from '@yeshuman/sdk'
import type { FilterDefinition, FilterOption } from '@yeshuman/sdk'
import { CalendarRange, Gauge, MapPin, Tag } from 'lucide-react'

import type { SymptomFilters } from '../hooks/use-symptoms'

export const SYMPTOM_SEVERITY_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
  { value: 'very_severe', label: 'Very severe' },
]

function severityValue(filters: SymptomFilters): string {
  return (Array.isArray(filters.severity) ? filters.severity[0] : filters.severity) ?? 'all'
}

export function getHealthSymptomFilterDefinitions(): FilterDefinition<SymptomFilters, undefined>[] {
  return [
    {
      key: 'severity',
      label: 'Severity',
      icon: Gauge,
      displayValue: (filters) =>
        SYMPTOM_SEVERITY_OPTIONS.find((o) => o.value === severityValue(filters))?.label ?? 'All',
      isNonDefault: (filters) => severityValue(filters) !== 'all',
      reset: (filters) => ({ ...filters, severity: undefined }),
      renderField: ({ filters, onChange }) => (
        <VisibleEnumSelect
          value={severityValue(filters)}
          options={SYMPTOM_SEVERITY_OPTIONS}
          onValueChange={(v) => onChange({ ...filters, severity: v === 'all' ? undefined : v })}
        />
      ),
      renderPopover: ({ filters, onChange }) => (
        <FilterOptionList
          type="single"
          options={SYMPTOM_SEVERITY_OPTIONS}
          value={severityValue(filters)}
          onValueChange={(v) => onChange({ ...filters, severity: v === 'all' ? undefined : v })}
        />
      ),
    },
    {
      key: 'date_range',
      label: 'Date range',
      icon: CalendarRange,
      displayValue: (filters) => formatDateRangeDisplay(filters, 'datetime'),
      isNonDefault: (filters) => isDateRangeActive(filters),
      reset: (filters) => ({ ...filters, start_date: undefined, end_date: undefined }),
      renderField: ({ filters, onChange }) => (
        <VisibleDateRangeField
          mode="datetime"
          value={filters}
          onChange={(range) => onChange({ ...filters, ...range })}
        />
      ),
      renderPopover: ({ filters, onChange }) => (
        <DateRangeControl
          mode="datetime"
          value={filters}
          onChange={(range) => onChange({ ...filters, ...range })}
        />
      ),
    },
    {
      key: 'symptom_type',
      label: 'Symptom',
      icon: Tag,
      displayValue: (filters) => filters.symptom_type ?? '',
      isNonDefault: (filters) => Boolean(filters.symptom_type),
      reset: (filters) => ({ ...filters, symptom_type: undefined }),
      renderField: ({ filters, onChange }) => (
        <VisibleTextField
          value={filters.symptom_type ?? ''}
          onValueChange={(v) => onChange({ ...filters, symptom_type: v || undefined })}
          placeholder="e.g., headache, fatigue"
        />
      ),
      renderPopover: ({ filters, onChange }) => (
        <TextFilterControl
          showOperator={false}
          operator="contains"
          value={filters.symptom_type ?? ''}
          onValueChange={(v) => onChange({ ...filters, symptom_type: v || undefined })}
          inputPlaceholder="e.g., headache, fatigue"
        />
      ),
    },
    {
      key: 'anatomy',
      label: 'Body location',
      icon: MapPin,
      displayValue: (filters) => filters.anatomy_name ?? '',
      isNonDefault: (filters) => Boolean(filters.anatomy_name),
      reset: (filters) => ({ ...filters, anatomy_name: undefined }),
      // Kept for legacy Pill / API; not in Visible Fields layout (cut to ≤3).
      renderPopover: ({ filters, onChange }) => (
        <TextFilterControl
          showOperator={false}
          operator="contains"
          value={filters.anatomy_name ?? ''}
          onValueChange={(v) => onChange({ ...filters, anatomy_name: v || undefined })}
          inputPlaceholder="e.g., head, chest"
        />
      ),
    },
  ]
}
